import React, { useMemo, useState, useEffect, useCallback } from "react";
import type { MenuProps } from "antd";
import { Flex, Layout, Spin } from "antd";
import { Navigate, useOutlet, useNavigate, useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

import AppSider from "components/app-sider/app-sider";
import MobileHeader from "components/mobile-header/mobile-header";
import SidebarIcon, { SidebarIconsSrc } from "components/sidebar-icon/sidebar-icon";
import OrganizationOnboardingModal from "components/organization-onboarding-modal/organization-onboarding-modal";
import { useUiStatus } from "state/reactive-vars/ui-status-var";
import routeNames from "constants/route-names";
import { HandoverStatus, NavProfile, UserRoleValue } from "constants/field-values";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { hasRoutePermission } from "routes/route-permissions";
import { useDiscardChatsOnNavigation } from "hooks/useDiscardChatsOnNavigation";
import { useNavProfile } from "hooks/useNavProfile";
import { useSubmittedHandoversCountSubscription } from "graphql/subscriptions/submitted-handovers-count.generated";

const featureFlagGuideEnabled = process.env.REACT_APP_FF_GUIDE_ENABLED == "true";
const featureFlagHandoversEnabled = process.env.REACT_APP_FF_HANDOVERS_ENABLED == "true";
const featureFlagIntelligenceEnabled = process.env.REACT_APP_FF_INTELLIGENCE_ENABLED == "true";
const featureFlagOllieEnabled = process.env.REACT_APP_FF_IRIS_ENABLED == "true";

const itemsData = [
	{
		label: "Dashboard",
		icon: SidebarIconsSrc.DASHBOARD,
		routeName: routeNames.DASHBOARD
	},
	...(featureFlagOllieEnabled
		? [
				{
					label: "Ollie",
					icon: SidebarIconsSrc.INTELLIGENCE,
					routeName: routeNames.OLLIE_DASHBOARD,
					// Every Ollie screen (inspections, evidence collection, plan review) lives under
					// /ollie, so the item stays selected across the whole flow.
					routeRegexTest: /^\/ollie(\/|$)/
				}
			]
		: []),
	...(featureFlagGuideEnabled
		? [
				{
					label: "Guides",
					icon: SidebarIconsSrc.GUIDES,
					routeName: routeNames.GUIDES,
					routeRegexTest: /^\/guides/
				}
			]
		: []),
	{
		label: "Devices",
		icon: SidebarIconsSrc.DEVICES,
		routeName: routeNames.INSTALLED_DEVICES,
		routeRegexTest: /^\/installed\-(devices*|products)/
	},
	// {
	//   label: "Notifications",
	//   icon: SidebarIconsSrc.NOTIFICATIONS,
	//   routeName: routeNames.NOTIFICATIONS,
	// },
	{
		label: "Request Service",
		icon: SidebarIconsSrc.REQUEST_SERVICE,
		routeName: routeNames.REQUEST_SERVICE
	},
	{
		label: "Order",
		icon: SidebarIconsSrc.ORDER,
		routeName: routeNames.ORDER,
		routeRegexTest: /^\/order|shop-products/
	},
	...(featureFlagHandoversEnabled
		? [
				{
					label: "Handovers",
					icon: SidebarIconsSrc.HANDOVER,
					routeName: routeNames.HANDOVERS,
					routeRegexTest: /^\/handovers|^\/handover-device-detection/
				}
			]
		: [])
];

// The Ollie profile has a single entry: its dashboard, which stays selected across every /ollie screen.
const ollieItemsData = [
	{
		label: "Dashboard",
		icon: SidebarIconsSrc.DASHBOARD,
		routeName: routeNames.OLLIE_DASHBOARD,
		routeRegexTest: /^\/ollie(\/|$)/
	}
];

const itemsDataByNavProfile: Record<NavProfile, typeof itemsData> = {
	[NavProfile.Default]: itemsData,
	[NavProfile.Ollie]: ollieItemsData
};

const adminItemsData = [
	{
		label: "Devices",
		icon: SidebarIconsSrc.DEVICES,
		routeName: routeNames.ADMIN_DEVICES,
		routeRegexTest: new RegExp(routeNames.ADMIN_DEVICES)
	},
	{
		label: "Properties",
		icon: SidebarIconsSrc.PROPERTIES,
		routeName: routeNames.ADMIN_PROPERTIES,
		routeRegexTest: /^\/admin-propert(y|ies)/
	},
	{
		label: "Users",
		icon: SidebarIconsSrc.USERS,
		routeName: routeNames.ADMIN_USERS,
		routeRegexTest: /^\/admin-users?/
	},
	{
		label: "Organizations",
		icon: SidebarIconsSrc.ORGANIZATIONS,
		routeName: routeNames.ADMIN_ORGANIZATIONS,
		routeRegexTest: /^\/admin-organizations?/
	},
	{
		label: "AI Models",
		icon: SidebarIconsSrc.INTELLIGENCE,
		routeName: routeNames.ADMIN_AI_MODELS,
		routeRegexTest: /^\/admin-ai-models/
	},
	{
		label: "Plan Review Rules",
		icon: SidebarIconsSrc.GUIDES,
		routeName: routeNames.PLAN_REVIEW_RULES,
		routeRegexTest: /^\/ollie\/plan-review\/rules/
	},
	{
		label: "Submission Types",
		icon: SidebarIconsSrc.TIERS,
		routeName: routeNames.PLAN_REVIEW_SUBMISSION_TYPES,
		routeRegexTest: /^\/ollie\/plan-review\/submission-types/
	},
	{
		label: "Ollie Fee Schedule",
		icon: SidebarIconsSrc.TIERS,
		routeName: routeNames.PLAN_REVIEW_FEE_SCHEDULE,
		routeRegexTest: /^\/ollie\/plan-review\/fee-schedule/
	},
	{
		label: "Review Categories",
		icon: SidebarIconsSrc.GUIDES,
		routeName: routeNames.PLAN_REVIEW_CATEGORIES,
		routeRegexTest: /^\/ollie\/plan-review\/categories/
	},
	{
		label: "Adopted Codes",
		icon: SidebarIconsSrc.GUIDES,
		routeName: routeNames.PLAN_REVIEW_CODES,
		routeRegexTest: /^\/ollie\/plan-review\/codes/
	},
	{
		label: "Code Imports",
		icon: SidebarIconsSrc.GUIDES,
		routeName: routeNames.PLAN_REVIEW_CODE_IMPORTS,
		routeRegexTest: /^\/ollie\/plan-review\/code-imports/
	},
	{
		label: "Authorities to Confirm",
		icon: SidebarIconsSrc.TIERS,
		routeName: routeNames.BOUNDARY_AUTHORITY_PROPOSALS,
		routeRegexTest: /^\/ollie\/boundary-authorities/
	},
	{
		label: "Jurisdiction Maps",
		icon: SidebarIconsSrc.ORGANIZATIONS,
		routeName: routeNames.BOUNDARY_LAYERS,
		routeRegexTest: /^\/admin-boundary-layers/
	},
	{
		label: "Jurisdiction Claims",
		icon: SidebarIconsSrc.ORGANIZATIONS,
		routeName: routeNames.ADMIN_AUTHORITY_CLAIMS,
		routeRegexTest: /^\/admin\/authority-claims/
	},
	...(featureFlagIntelligenceEnabled
		? [
				{
					label: "Intelligence",
					icon: SidebarIconsSrc.INTELLIGENCE,
					routeName: routeNames.ADMIN_INTELLIGENCE
				}
			]
		: [])
];

// Roles that get the Admin submenu. Which entries it holds is a per-route question the
// filter below answers: AHJ staff open it on the three plan review screens alone, with
// no Users or Organizations page behind it.
const ADMIN_ROLES = [
	UserRoleValue.admin_user,
	UserRoleValue.vendor_manager,
	UserRoleValue.vendor_employee,
	UserRoleValue.customer_manager,
	UserRoleValue.authority_manager,
	UserRoleValue.authority_user
];

const LoggedNavigationLayout: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { uiStatusValue, updateUiStatus } = useUiStatus();
	const { loggedSidebarCollapsed } = uiStatusValue;
	const location = useLocation();
	const { pathname } = location;
	const navigate = useNavigate();
	const outlet = useOutlet();
	const userRole = loggedUserValue?.role as UserRoleValue;
	const isAdminRole = ADMIN_ROLES.includes(userRole);
	const { navProfile, isNavProfileResolved } = useNavProfile();
	const visibleItemsData = itemsDataByNavProfile[navProfile.id];

	// This layout outlives the routes below it, so it can tell a navigation from a mere unmount.
	const allPropertyIds = useMemo(
		() => (loggedUserValue.user_properties || []).map(userProperty => userProperty.property_id),
		[loggedUserValue.user_properties]
	);
	useDiscardChatsOnNavigation(allPropertyIds);

	const selected = visibleItemsData.find(element => pathname === element.routeName);
	// Admin routes are the specific ones: /ollie/plan-review/rules also matches the
	// Ollie regex, so it has to be resolved here first or both light up.
	const selectedAdminRoute = adminItemsData.find(item =>
		item.routeRegexTest ? item.routeRegexTest.test(pathname) : pathname === item.routeName
	);
	const [isSubMenuOpen, setIsSubMenuOpen] = useState<boolean>(!!selectedAdminRoute);
	const [selectedKey, setSelectedKey] = useState<string>((selected || selectedAdminRoute)?.routeName || "");

	// The count only lights up the Handovers item in the side nav. A role that does not have that
	// route has no handovers to count and no permission to read them either — and this layout wraps
	// every logged-in screen, so asking anyway fails the subscription on all of them.
	const canSeeHandovers =
		hasRoutePermission(routeNames.HANDOVERS, userRole) &&
		visibleItemsData.some(item => item.routeName === routeNames.HANDOVERS);

	const { data: submittedHandoversData } = useSubmittedHandoversCountSubscription({
		skip: !canSeeHandovers,
		variables: {
			customer_status: [HandoverStatus.PENDING_CUSTOMER],
			vendor_status: [HandoverStatus.PENDING_VENDOR],
			organization_ids: [loggedUserValue.organization_id]
		}
	});
	const hasSubmittedHandovers = (submittedHandoversData?.handover_aggregate?.aggregate?.count ?? 0) > 0;

	useEffect(() => {
		if (pathname) {
			const selectedItem =
				selectedAdminRoute ??
				visibleItemsData.find(element => {
					if (element.routeRegexTest) {
						return element.routeRegexTest.test(pathname);
					}
					return pathname === element.routeName;
				});
			if (selectedItem) {
				if (selectedKey !== selectedItem.routeName) {
					setSelectedKey(selectedItem.routeName);
				}
			} else {
				setSelectedKey("");
			}
		}
	}, [pathname, selectedKey, selectedAdminRoute]);

	useEffect(() => {
		if (loggedSidebarCollapsed) {
			setIsSubMenuOpen(false);
		} else if (!!selectedAdminRoute) {
			setIsSubMenuOpen(true);
		}
	}, [loggedSidebarCollapsed, selectedAdminRoute]);

	const handleOnClickLogo = useCallback(() => {
		navigate(navProfile.homeRoute);
	}, [navigate, navProfile.homeRoute]);

	const handleMenuItemClick = useCallback<NonNullable<MenuProps["onClick"]>>(
		({ key }) => {
			setSelectedKey(key);
			navigate(key);
			if (isMobile) {
				updateUiStatus({ loggedSidebarCollapsed: true });
			}
		},
		[navigate, updateUiStatus]
	);

	const menuItems: MenuProps["items"] = useMemo(() => {
		const items = visibleItemsData
			.filter(item => {
				// If you are admin, see everything
				if (userRole === UserRoleValue.admin_user) return true;

				// Check permits for the route
				return hasRoutePermission(item.routeName, userRole);
			})
			.map(element => {
				const { label, routeName, icon } = element;
				const isSelected = selectedKey === routeName;
				return {
					title: label,
					label: (
						<div className="sidebar-label-container">
							<SidebarIcon
								icon={icon}
								isSelected={isSelected}
								showNotificationIcon={routeName === routeNames.HANDOVERS && hasSubmittedHandovers}
							/>
							<span className="ant-menu-title-content">{label}</span>
						</div>
					),
					key: routeName
				};
			});

		if (isAdminRole) {
			const filteredAdminItems = adminItemsData
				.filter(item => {
					if (userRole === UserRoleValue.admin_user) return true;
					return hasRoutePermission(item.routeName, userRole);
				})
				.map(element => {
					const { label, routeName, icon } = element;
					return {
						label,
						key: routeName
					};
				});

			// checks child pages. e.g. admin-property/:id
			const isAdminPage = !!selectedAdminRoute || /admin/.test(pathname);
			// antd highlights the open submenu's child from selectedKeys, so the active
			// screen inside Admin marks itself once selectedKey points at it.

			items.push({
				label: "Admin",
				key: "admin",
				className: !!isAdminPage ? "admin-route-active" : "",
				icon: <SidebarIcon icon={SidebarIconsSrc.ADMIN} isSelected={!!isAdminPage} />,
				children: filteredAdminItems,
				onTitleClick: () => {
					setIsSubMenuOpen(prev => !prev);
				}
			} as any);
		}

		return items;
	}, [pathname, userRole, isAdminRole, isSubMenuOpen, hasSubmittedHandovers, selectedKey, selectedAdminRoute]);

	// Until the profile is known, showing either navigation could be the wrong one.
	if (!isNavProfileResolved) {
		return (
			<Flex justify="center" align="center" style={{ height: "100vh" }}>
				<Spin />
			</Flex>
		);
	}

	// The customer dashboard is the default landing route (login, email verification, sign up).
	// A profile with another home screen sends that landing to its own home.
	const isOnDefaultLanding = pathname === routeNames.DASHBOARD;
	if (isOnDefaultLanding && navProfile.homeRoute !== routeNames.DASHBOARD) {
		return <Navigate to={navProfile.homeRoute} replace />;
	}

	// A self-onboarded user (role basic) still belongs to the shared Free organization.
	// The Ollie profile is the only place this flow is offered today, so the mandatory
	// org-creation modal (MWA-838) replaces the whole screen until it is completed.
	const needsOrganizationOnboarding = navProfile.id === NavProfile.Ollie && userRole === UserRoleValue.basic;
	if (needsOrganizationOnboarding) {
		return <OrganizationOnboardingModal />;
	}

	return (
		<div className="logged-nav-container">
			<Layout className="logged-nav-layout">
				<AppSider
					isCollapsed={!!loggedSidebarCollapsed}
					menuItems={menuItems}
					selectedKey={selectedKey}
					selectedSubmenu={isSubMenuOpen ? "admin" : ""}
					handleOnCollapse={collapsed => {
						updateUiStatus({
							loggedSidebarCollapsed: collapsed
						});
					}}
					toggle={() => {
						updateUiStatus({
							loggedSidebarCollapsed: !loggedSidebarCollapsed
						});
					}}
					handleClose={() => {
						updateUiStatus({
							loggedSidebarCollapsed: true
						});
					}}
					handleOnClickLogo={handleOnClickLogo}
					onMenuItemClick={handleMenuItemClick}
				/>

				<div className="logged-nav-content">
					<MobileHeader />
					<div className="logged-nav-screen">{outlet}</div>
				</div>
			</Layout>
		</div>
	);
};

export default LoggedNavigationLayout;
